import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, EMPTY, Observable, tap } from 'rxjs';

import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { withNoMessageToast } from '../../core/interceptors/http-toast.interceptor';
import { UserService } from '../../core/services/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  router = inject(Router);
  userSvc = inject(UserService);
  constructor(private http: HttpClient) {}

  login(email: string, password: string): Observable<any> {
    const url = `${environment.baseApiUrl}/api/login`;
    return this.http
      .post<any>(url, { email, password }, { context: withNoMessageToast() })
      .pipe(
        tap((session) => {
          // If backend requires the user to change password, redirect to change-password
          if (session?.mustChangePassword) {
            // don't persist session unless tokens are provided
            this.router.navigate(['/change-password'], {
              queryParams: {
                token: session?.token,
              },
            });
            return;
          }

          // Only save session when a token (and optionally refreshToken) is present
          const token = session?.data?.token ?? session?.token ?? null;
          const refreshToken =
            session?.data?.refreshToken ?? session?.refreshToken ?? null;
          if (token && refreshToken) {
            this.saveSession(session);
          }
        })
      );
  }

  logout(forse: boolean = false): Observable<any> {
    if (forse) {
      localStorage.removeItem('zuntime_session');
      this.router.navigate(['/login']);
      return EMPTY;
    } else {
      const url = `${environment.baseApiUrl}/api/logout`;
      const refreshToken = this.getRefreshToken();
      return this.http
        .post<any>(url, { refreshToken }, { context: withNoMessageToast() })
        .pipe(
          tap(() => {
            localStorage.removeItem('zuntime_session');
            this.router.navigate(['/login']);
          }),
          catchError(() => {
            localStorage.removeItem('zuntime_session');
            this.router.navigate(['/login']);
            return EMPTY;
          })
        );
    }
  }

  saveSession(data: any): void {
    if (data) {
      try {
        localStorage.setItem('zuntime_session', JSON.stringify(data));
      } catch {
        // ignore storage errors
      }
    }
  }

  isUserLoggedIn(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    const session = localStorage.getItem('zuntime_session');
    if (!session) return null;
    try {
      const data = JSON.parse(session);
      return data.data.token || null;
    } catch {
      return null;
    }
  }
  getRefreshToken(): string | null {
    const session = localStorage.getItem('zuntime_session');
    if (!session) return null;
    try {
      const data = JSON.parse(session);
      return data.data.refreshToken || null;
    } catch {
      return null;
    }
  }

  refreshTokenRequest(): Observable<any> {
    const url = `${environment.baseApiUrl}/api/auth/refresh`;
    const refreshToken = this.getRefreshToken();
    return this.http
      .post<any>(url, { refreshToken }, { context: withNoMessageToast() })
      .pipe(
        tap((token) => {
          const session = JSON.parse(
            localStorage.getItem('zuntime_session') || '{}'
          );
          session.data.token = token.token;
          this.saveSession(session);
        })
      );
  }

  changePassword(newPassword: string, token: string) {
    const url = `${environment.baseApiUrl}/api/auth/password-reset`;
    return this.http.post<any>(url, { newPassword, token });
  }

  requestPasswordReset(email: string) {
    const url = `${environment.baseApiUrl}/api/auth/password-reset/request`;
    return this.http.post<any>(url, { email });
  }

  validateRequestPasswordReset(token: string) {
    const url = `${environment.baseApiUrl}/api/auth/password-reset/validate`;
    return this.http.get<any>(url, { params: { token } });
  }

  // Methods to handle intended route redirection
  saveIntendedRoute(route: string): void {
    try {
      localStorage.setItem('zuntime_intended_route', route);
    } catch {
      // ignore storage errors
    }
  }

  getIntendedRoute(): string | null {
    try {
      return localStorage.getItem('zuntime_intended_route');
    } catch {
      return null;
    }
  }

  clearIntendedRoute(): void {
    try {
      localStorage.removeItem('zuntime_intended_route');
    } catch {
      // ignore storage errors
    }
  }

  getRedirectRoute(): string {
    const intendedRoute = this.getIntendedRoute();
    if (intendedRoute) {
      this.clearIntendedRoute();
      return intendedRoute;
    }
    return this.userSvc.routeToRedirectWithPermission() || '/';
  }
}
